import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Pencil, Plus, Printer, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import DayTimeline from "@/components/DayTimeline";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Panel, Empty } from "@/pages/shared";

const DAY_KEYS = ["day_mon", "day_tue", "day_wed", "day_thu", "day_fri", "day_sat", "day_sun"];
const iso = (d) => d.toISOString().slice(0, 10);
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const hhmm = (v) => (v ? new Date(v).toTimeString().slice(0, 5) : "");
const hrs = (v) => `${Number(v || 0).toFixed(2)}u`;

export default function Timesheets() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const [view, setView] = useState("week");
  const [day, setDay] = useState(() => iso(new Date()));
  const [weekStart, setWeekStart] = useState(() => iso(monday(new Date())));
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null); // {user_id, name, date, entries}
  const [form, setForm] = useState(null); // {id?, user_id, date, start, end, note}
  const [mail, setMail] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/timesheets/mail-settings").then(({ data }) => setMail(data)).catch(() => {});
  }, [isAdmin]);

  const saveMail = async (next) => {
    setMail(next);
    try { await api.put("/timesheets/mail-settings", next); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    try {
      const url = view === "week" ? `/timesheets/week?start=${weekStart}` : `/timesheets/month?month=${month}`;
      setData((await api.get(url)).data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [view, weekStart, month]);
  useEffect(() => { load(); }, [load]);

  const shiftWeek = (days) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + days); setWeekStart(iso(monday(d)));
  };

  const openDetail = async (row, date) => {
    try {
      const { data: entries } = await api.get(`/timesheets/entries?date=${date}&user_id=${row.user_id}`);
      setDetail({ user_id: row.user_id, name: row.name, date, entries });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    const { data: entries } = await api.get(`/timesheets/entries?date=${detail.date}&user_id=${detail.user_id}`);
    setDetail({ ...detail, entries });
    load();
  };

  const saveEntry = async () => {
    try {
      const body = { user_id: form.user_id, date: form.date, start: form.start, end: form.end, note: form.note || "" };
      if (form.id) await api.put(`/timesheets/entries/${form.id}`, body);
      else await api.post("/timesheets/entries", body);
      setForm(null); await refreshDetail(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const removeEntry = async (id) => {
    try { await api.delete(`/timesheets/entries/${id}`); await refreshDetail(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const download = async () => {
    try {
      const q = view === "week" ? `start=${weekStart}` : `month=${month}`;
      const res = await api.get(`/timesheets/export?${q}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `uren-${view === "week" ? weekStart : month}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const dates = data?.dates || [];
  const weekMode = view === "week";

  return (
    <Panel
      title={isAdmin ? t("admin_hours") : t("my_hours")}
      testid="timesheets-panel"
      action={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <div className="flex rounded-full border border-border overflow-hidden">
            {["day", "week", "month"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`hours-view-${v}`}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${view === v ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                {t(v === "week" ? "hours_week" : v === "month" ? "hours_month" : "hours_day")}
              </button>
            ))}
          </div>
          {view === "day" ? (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setDay(iso(new Date(new Date(day).getTime() - 86400000)))} data-testid="hours-prev-day"><ChevronLeft className="h-4 w-4" /></Button>
              <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-40" data-testid="hours-day-input" />
              <Button variant="outline" size="icon" onClick={() => setDay(iso(new Date(new Date(day).getTime() + 86400000)))} data-testid="hours-next-day"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          ) : weekMode ? (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => shiftWeek(-7)} data-testid="hours-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
              <Input type="date" value={weekStart} onChange={(e) => setWeekStart(iso(monday(new Date(e.target.value))))} className="w-40" data-testid="hours-week-input" />
              <Button variant="outline" size="icon" onClick={() => shiftWeek(7)} data-testid="hours-next-week"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          ) : (
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="hours-month-input" />
          )}
          <Button variant="outline" onClick={download} data-testid="hours-csv"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="hours-print"><Printer className="h-4 w-4 mr-1" />{t("print")}</Button>
        </div>
      }
    >
      <div className="p-5 space-y-4">
        <p className="text-sm text-muted-foreground">{isAdmin ? t("hours_desc") : t("hours_desc_own")}</p>
        {view === "day" && <DayTimeline date={day} />}
        {isAdmin && mail && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-muted/40 p-3" data-testid="hours-mail-settings">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={mail.enabled} data-testid="hours-mail-enabled"
                onChange={(e) => saveMail({ ...mail, enabled: e.target.checked })} />
              {t("hours_mail_enabled")}
            </label>
            <div className="space-y-1">
              <Label className="text-xs">{t("hours_mail_day")}</Label>
              <select value={mail.weekday} data-testid="hours-mail-day"
                onChange={(e) => saveMail({ ...mail, weekday: Number(e.target.value) })}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm">
                {DAY_KEYS.map((k, i) => <option key={k} value={i}>{t(k)}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("hours_mail_hour")}</Label>
              <Input type="number" min="0" max="23" value={mail.hour} className="w-20" data-testid="hours-mail-hour"
                onChange={(e) => setMail({ ...mail, hour: Number(e.target.value) })}
                onBlur={() => saveMail(mail)} />
            </div>
            <p className="text-[11px] text-muted-foreground flex-1 min-w-[14rem]">{t("hours_mail_hint")}</p>
          </div>
        )}
        {view === "day" ? null : !data || data.rows.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto">
            <Table data-testid="hours-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("u_nickname")}</TableHead>
                  {weekMode
                    ? dates.map((d, i) => <TableHead key={d} className="text-right">{t(DAY_KEYS[i])}<span className="block text-[10px] text-muted-foreground">{d.slice(8)}/{d.slice(5, 7)}</span></TableHead>)
                    : <TableHead className="text-right">{t("hours_days_worked")}</TableHead>}
                  <TableHead className="text-right">{t("hours_worked")}</TableHead>
                  <TableHead className="text-right">{t("hours_planned")}</TableHead>
                  <TableHead className="text-right">{t("hours_diff")}</TableHead>
                  <TableHead className="text-right">{t("hours_min")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`hours-row-${r.number || r.user_id}`}>
                    <TableCell className="font-medium whitespace-nowrap">{r.nickname || r.name}</TableCell>
                    {weekMode
                      ? r.days.map((d) => (
                        <TableCell key={d.date} className="text-right tabular-nums">
                          <button onClick={() => openDetail(r, d.date)} data-testid={`hours-cell-${r.number || r.user_id}-${d.date}`}
                            className={`px-1.5 py-0.5 rounded hover:bg-muted ${d.worked ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                            {d.worked ? d.worked.toFixed(2) : "—"}
                            {d.breaks > 0 && <span className="ml-1 text-[10px] text-amber-600">{d.breaks}×</span>}
                          </button>
                        </TableCell>
                      ))
                      : <TableCell className="text-right tabular-nums">{r.days.filter((d) => d.worked > 0).length}</TableCell>}
                    <TableCell className="text-right tabular-nums font-semibold">{hrs(r.worked)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{hrs(r.planned)}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${r.difference < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {r.difference > 0 ? "+" : ""}{r.difference.toFixed(2)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${r.min_week_hours && r.worked < r.min_week_hours && weekMode ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
                      {r.min_week_hours ? hrs(r.min_week_hours) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="hours-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">{detail?.name} — {detail?.date}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(detail?.entries || []).length === 0 && <p className="text-sm text-muted-foreground">{t("hours_none")}</p>}
            {(detail?.entries || []).map((e) => (
              <div key={e.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm" data-testid={`hours-entry-${e.id}`}>
                <div>
                  <span className="font-semibold tabular-nums">{hhmm(e.start)} – {e.end ? hhmm(e.end) : t("hours_open")}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {e.reason === "break" ? t("break") : e.reason === "idle" ? t("hours_idle") : e.reason === "auto" ? t("hours_auto") : e.manual ? t("hours_manual") : t("logout")}
                  </span>
                  {e.note && <p className="text-xs text-muted-foreground">{e.note}</p>}
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" data-testid={`hours-edit-${e.id}`}
                      onClick={() => setForm({ id: e.id, user_id: e.user_id, date: e.date, start: hhmm(e.start), end: hhmm(e.end), note: e.note || "" })}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => removeEntry(e.id)} data-testid={`hours-delete-${e.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {isAdmin && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setForm({ user_id: detail.user_id, date: detail.date, start: "09:00", end: "17:00", note: "" })} data-testid="hours-add">
                <Plus className="h-4 w-4 mr-1" />{t("hours_add")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="hours-form-dialog">
          <DialogHeader><DialogTitle className="font-display">{form?.id ? t("edit") : t("hours_add")}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2"><Label className="text-xs">{t("date")}</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="hours-form-date" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("from")}</Label>
                <Input required type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="hours-form-start" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("to")}</Label>
                <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="hours-form-end" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs">{t("note")}</Label>
                <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="hours-form-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("cancel")}</Button>
            <Button onClick={saveEntry} disabled={!form?.start} data-testid="hours-form-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
